import React, { useState } from "react";
import { Link } from "react-router-dom";
import { memberApi } from "@/lib/memberAuth";
import { toast } from "sonner";
import { EnvelopeSimple, ArrowLeft } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

export default function MemberForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await memberApi.post("/member/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center p-6 relative overflow-hidden" data-testid="member-forgot-page">
      <div className="absolute inset-0 grid-bg opacity-20" />
      <div className="relative w-full max-w-md bg-white p-10 border border-black/5 shadow-2xl">
        <div className="mb-8">
          <Logo size="md" asLink={false} showTagline={false} testId="forgot-logo" />
          <div className="mt-2 text-[10px] uppercase tracking-[0.24em] text-navy/50">Reset password</div>
        </div>

        {done ? (
          <div data-testid="forgot-success">
            <h1 className="font-display text-2xl font-bold text-navy tracking-tight">Check your inbox</h1>
            <p className="text-sm text-navy/70 mt-3 leading-relaxed">
              If <span className="font-mono">{email}</span> is registered on the EPAS network, we&apos;ve generated a one-time
              secure reset link. It expires in 24 hours.
            </p>
            <p className="text-[11px] text-navy/50 mt-4 leading-relaxed">
              EPAS never sends passwords in email — always a one-time link. If you don&apos;t receive it, check with{" "}
              <a href="mailto:rok@epas.one" className="underline hover:text-ember">rok@epas.one</a>.
            </p>
            <Link
              to="/portal/login"
              data-testid="forgot-back-login"
              className="mt-6 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-navy hover:text-ember"
            >
              <ArrowLeft size={12} /> Back to sign-in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-bold text-navy tracking-tight">Forgot password</h1>
            <p className="text-sm text-navy/60 mt-2">
              We&apos;ll send a one-time secure link to reset your password. No plaintext passwords, ever.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="overline mb-2 block">Email</label>
                <input
                  data-testid="forgot-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-black/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
                  required
                />
              </div>
              <button type="submit" disabled={loading} data-testid="forgot-submit" className="btn-primary w-full justify-center">
                <EnvelopeSimple size={16} /> {loading ? "Sending…" : "Send reset link"}
              </button>
            </form>
            <Link to="/portal/login" data-testid="forgot-cancel" className="mt-6 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-navy/60 hover:text-navy">
              <ArrowLeft size={12} /> Cancel
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
